import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Send, Coins, ArrowRight, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";

const TONES = [
  { value: "direct", label: "Direct", hint: "No fluff. 2 sentences." },
  { value: "consultative", label: "Consultative", hint: "Asks a smart question." },
  { value: "friendly", label: "Friendly", hint: "Warm and casual." },
];

/**
 * DraftEmailDialog — generates an AI email draft for one lead, lets the
 * user edit it, then sends via their stored SMTP. Consumes 1 email credit
 * per draft generation.
 */
export default function DraftEmailDialog({ open, onClose, lead }) {
  const [step, setStep] = useState("setup"); // setup | drafting | edit
  const [tone, setTone] = useState("consultative");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [smtpReady, setSmtpReady] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("setup");
    setTone("consultative");
    setSubject("");
    setBody("");
    setDraftId(null);
    // Pre-fill recipient from buying committee if available
    const committee = lead?.enriched_data?.buying_committee || lead?.enrichment?.buying_committee || [];
    const first = committee.find((c) => c.email) || committee[0];
    setTo(first?.email || lead?.input?.email || lead?.email || "");
    // Load balance + smtp status
    Promise.all([
      http.get("/outreach/credits/balance").then((r) => r.data).catch(() => null),
      http.get("/outreach/smtp").then((r) => r.data.config).catch(() => null),
    ]).then(([bal, smtp]) => {
      setBalance(bal);
      setSmtpReady(!!smtp);
    });
  }, [open, lead]);

  const generate = async () => {
    if (!balance || balance.balance <= 0) {
      return toast.error("No email credits. Buy a pack first.");
    }
    setStep("drafting");
    try {
      const { data } = await http.post("/outreach/draft", { lead_id: lead.id, tone });
      setSubject(data.draft.subject || "");
      setBody(data.draft.body || "");
      setDraftId(data.draft.id);
      setBalance(data.balance);
      setStep("edit");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Draft failed");
      setStep("setup");
    }
  };

  const send = async () => {
    if (!smtpReady) return toast.error("Connect your SMTP in Settings → Outreach first");
    if (!to || !subject || !body) return toast.error("To / Subject / Body all required");
    setSending(true);
    try {
      await http.post("/outreach/send", {
        draft_id: draftId,
        to: to.trim().toLowerCase(),
        subject: subject.trim(),
        body,
      });
      toast.success("Email sent");
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const company = lead?.input?.company || lead?.company || "this prospect";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-2xl rounded-sm border-ink-100 p-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-ink-100">
          <DialogTitle className="flex items-center gap-3">
            <div className="grid place-items-center w-8 h-8 rounded-sm bg-ink-900 text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                AI draft
              </div>
              <div className="font-display font-extrabold text-lg text-ink-900">
                Email {company}
              </div>
            </div>
          </DialogTitle>
          {balance && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono text-ink-500 absolute right-6 top-7">
              <Coins className="w-3 h-3" />
              {balance.balance.toLocaleString()} credits
            </div>
          )}
        </DialogHeader>

        <div className="px-6 py-5 overflow-y-auto flex-1" data-testid="draft-email-dialog">
          {/* Setup step */}
          {step === "setup" && (
            <>
              {balance?.balance === 0 && (
                <NoCreditsBanner onClose={onClose} />
              )}
              {smtpReady === false && (
                <SmtpRequiredBanner onClose={onClose} />
              )}
              <div className="mb-5">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  To
                </Label>
                <Input
                  data-testid="draft-to-input"
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@company.com"
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
              <div className="mb-6">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Tone
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {TONES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      data-testid={`tone-${t.value}`}
                      className={`text-left p-3 rounded-sm border transition-colors ${
                        tone === t.value
                          ? "border-ink-900 bg-ink-50"
                          : "border-ink-100 hover:border-ink-300"
                      }`}
                    >
                      <div className="font-semibold text-sm text-ink-900">{t.label}</div>
                      <div className="text-[11px] text-ink-500 mt-0.5">{t.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={generate}
                disabled={!to || balance?.balance === 0 || !smtpReady}
                className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
                data-testid="draft-generate-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate draft · 1 credit
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </>
          )}

          {/* Drafting step */}
          {step === "drafting" && (
            <div className="py-16 text-center" data-testid="draft-loading">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-ink-900 mb-4" />
              <div className="text-sm text-ink-500">
                Composing your email using the enrichment signals…
              </div>
            </div>
          )}

          {/* Edit step */}
          {step === "edit" && (
            <>
              <div className="mb-4">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  To
                </Label>
                <Input
                  data-testid="edit-to-input"
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
              <div className="mb-4">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Subject
                </Label>
                <Input
                  data-testid="edit-subject-input"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-2 rounded-sm h-11"
                />
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                    Body
                  </Label>
                  <button
                    type="button"
                    onClick={() => {
                      const url = window.prompt(
                        "Paste a link to insert into the email body (Calendly, demo page, case study URL, etc.):",
                        "https://"
                      );
                      if (!url || url === "https://") return;
                      const safe = url.startsWith("http") ? url : `https://${url}`;
                      setBody((b) => (b ? `${b.replace(/\s+$/, "")}\n\n${safe}\n` : `${safe}\n`));
                      toast.success("Link added — clicks will be tracked automatically.");
                    }}
                    data-testid="add-tracked-link-btn"
                    className="text-[10px] uppercase tracking-widestx font-semibold text-brand hover:text-brand-hover inline-flex items-center gap-1"
                  >
                    <LinkIcon className="w-3 h-3" />
                    Add tracked link
                  </button>
                </div>
                <Textarea
                  data-testid="edit-body-textarea"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="mt-2 rounded-sm font-mono text-sm leading-relaxed"
                />
              </div>
              <div className="text-[11px] text-ink-500 mb-5 leading-relaxed">
                <span className="font-mono">
                  {body.split(/\s+/).filter(Boolean).length} words
                </span>
                <span className="mx-2 text-ink-300">·</span>
                <span className="text-ink-700">
                  Any <code className="px-1 bg-ink-100 rounded">https://</code> URL in the body is
                  auto-rewritten to a tracked link so you can see clicks on the Dashboard. Opens
                  use a 1×1 pixel — if Gmail/Outlook strips it, replies now still count as opens.
                </span>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setStep("setup")}
                  variant="outline"
                  className="h-11 px-4 rounded-sm border-ink-200 hover:border-ink-900 text-ink-700"
                  data-testid="draft-regenerate-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Regenerate (1 credit)
                </Button>
                <Button
                  onClick={send}
                  disabled={sending}
                  className="flex-1 h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide"
                  data-testid="draft-send-button"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-2" /> Send via your SMTP</>)}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoCreditsBanner({ onClose }) {
  return (
    <div className="mb-5 border border-[#FFC107] bg-[#FFFBEB] rounded-sm p-4" data-testid="no-credits-banner">
      <div className="font-semibold text-sm text-ink-900 mb-1">You're out of email credits</div>
      <div className="text-[12px] text-ink-600 mb-3">
        $5 = 100 credits = 100 AI-drafted emails. Sending is free.
      </div>
      <Link
        to="/settings/outreach"
        onClick={onClose}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
      >
        Buy credits → <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function SmtpRequiredBanner({ onClose }) {
  return (
    <div className="mb-5 border border-[#FFC107] bg-[#FFFBEB] rounded-sm p-4" data-testid="smtp-required-banner">
      <div className="font-semibold text-sm text-ink-900 mb-1">Connect your email first</div>
      <div className="text-[12px] text-ink-600 mb-3">
        We send via your own SMTP (Gmail, Outlook, Zoho, anything). All replies go to your inbox.
      </div>
      <Link
        to="/settings/outreach"
        onClick={onClose}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
      >
        Connect SMTP → <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
